/**
 * Procesamiento API Service
 * Cola de trabajo por área y captura manual de resultados de exámenes.
 */

import { api } from "@/lib/api";
import type { PaginatedResponse } from "@/hooks/use-server-data-table";

// =============================================================================
// URLS
// =============================================================================

const COLA_TRABAJO_URL = "/cola-trabajo/";
const RESULTADOS_URL = "/resultados/";

// =============================================================================
// TYPES — Enums / Choices
// =============================================================================

export type FlagAnormalidad =
  | "BAJO"
  | "NORMAL"
  | "ALTO"
  | "CRITICO_BAJO"
  | "CRITICO_ALTO"
  | "NO_APLICA";

export type MetodoCaptura = "MANUAL" | "AUTOMATICO";

export type AreaLaboratorio =
  | "HEMATOLOGIA"
  | "QUIMICA"
  | "SEROLOGIA"
  | "INMUNOLOGIA"
  | "URINALISIS"
  | "COAGULACION"
  | "MICROBIOLOGIA"
  | "PARASITOLOGIA"
  | "HORMONAS"
  | "MARCADORES"
  | "DROGAS"
  | "ESPECIAL";

export type TipoResultado = "NUMERICO" | "TEXTO" | "OPCIONES" | "FORMULA";

export type EstadoOrdenExamen =
  | "PENDIENTE"
  | "EN_PROCESO"
  | "CON_RESULTADO"
  | "VALIDADO"
  | "CANCELADO";

export type PrioridadOrden = "NORMAL" | "URGENTE" | "STAT";

// =============================================================================
// TYPES — Valor de Referencia (nested en Resultado)
// =============================================================================

export interface ValorReferenciaItem {
  id: number;
  sexo: "M" | "F" | "A";
  sexo_display: string;
  edad_min_dias: number;
  edad_max_dias: number;
  rango_edad_str: string;
  valor_min: string | null;
  valor_max: string | null;
  unidad: string;
  descripcion: string;
  rango_str: string;
}

// =============================================================================
// TYPES — Resultado
// =============================================================================

export interface Resultado {
  id: number;
  orden_examen: number;
  examen_nombre: string;
  examen_codigo: string;
  examen_area: string;
  tipo_resultado: TipoResultado;
  opciones_resultado: string[];
  valor_numerico: string | null;
  valor_texto: string;
  valor_opcion: string;
  unidad: string;
  observaciones: string;
  flag_anormalidad: FlagAnormalidad;
  flag_anormalidad_display: string;
  metodo_captura: MetodoCaptura;
  metodo_captura_display: string;
  capturado_por: number | null;
  capturado_por_nombre: string | null;
  fecha_captura: string;
  valores_referencia: ValorReferenciaItem[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// TYPES — Cola de Trabajo
// =============================================================================

export interface ColaTrabajoItem {
  id: number;
  estado: EstadoOrdenExamen;
  estado_display: string;
  prioridad: PrioridadOrden;
  prioridad_display: string;
  prioridad_orden: number;
  // Paciente
  paciente_id: number;
  paciente_nombre: string;
  paciente_documento: string;
  // Orden
  numero_orden: string;
  orden_id: number;
  created_at: string;
  // Examen
  examen_id: number;
  examen_nombre: string;
  examen_codigo: string;
  examen_area: AreaLaboratorio;
  examen_area_display: string;
  tipo_resultado: TipoResultado;
  // Muestra
  codigo_barras_muestra: string | null;
  // Tiempo
  tiempo_espera_minutos: number;
}

export interface ContadorArea {
  area: AreaLaboratorio;
  area_display: string;
  conteo: number;
}

// =============================================================================
// TYPES — Filtros de Cola
// =============================================================================

export interface ColaTrabajoFilters {
  sucursal: number;
  area?: AreaLaboratorio | "";
  estado?: EstadoOrdenExamen | "";
  prioridad?: PrioridadOrden | "";
  search?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  page?: number;
  page_size?: number;
}

// =============================================================================
// TYPES — Input DTOs
// =============================================================================

export interface ResultadoCreateData {
  orden_examen_id: number;
  valor_numerico?: string | null;
  valor_texto?: string;
  valor_opcion?: string;
  unidad?: string;
  observaciones?: string;
}

export interface ResultadoUpdateData {
  valor_numerico?: string | null;
  valor_texto?: string;
  valor_opcion?: string;
  unidad?: string;
  observaciones?: string;
}

// =============================================================================
// API — Cola de Trabajo
// =============================================================================

export async function getColaTrabajo(
  filters: ColaTrabajoFilters
): Promise<PaginatedResponse<ColaTrabajoItem>> {
  // Remove empty string values (keep undefined for optional params)
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== "")
  );
  const response = await api.get(COLA_TRABAJO_URL, { params: cleanFilters });
  return response.data;
}

export async function getContadoresArea(sucursalId: number): Promise<ContadorArea[]> {
  const response = await api.get(`${COLA_TRABAJO_URL}contadores/`, {
    params: { sucursal: sucursalId },
  });
  return response.data;
}

// =============================================================================
// API — Resultados
// =============================================================================

export async function getResultado(ordenExamenId: number): Promise<Resultado> {
  const response = await api.get(`${RESULTADOS_URL}${ordenExamenId}/`);
  return response.data;
}

export async function capturarResultado(data: ResultadoCreateData): Promise<Resultado> {
  const response = await api.post(RESULTADOS_URL, data);
  return response.data;
}

export async function corregirResultado(
  ordenExamenId: number,
  data: ResultadoUpdateData
): Promise<Resultado> {
  const response = await api.put(`${RESULTADOS_URL}${ordenExamenId}/`, data);
  return response.data;
}
